import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { submitContact } from "@/lib/api";

const EASE = [0.76, 0, 0.24, 1];

const COMPANY_SIZES = ["1–50", "51–150", "151–500", "501–1,500", "1,500+"];
const SEGMENT_OPTIONS = [
  "GCC — building or scaling an India centre",
  "AI-native IT services / product company",
  "Other",
];
const CHALLENGES = [
  "Hiring speed / quality",
  "HR operations & compliance",
  "Compensation & org structure",
  "Employer brand",
  "Senior HR leadership (Fractional CHRO)",
  "Something else",
];
const TIMELINES = ["Immediately", "This quarter", "Next quarter", "Exploring"];

const selectCls =
  "editorial-input appearance-none cursor-pointer bg-[var(--bg)]";

export default function BookDiagnosticPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    company_size: "",
    segment: "",
    challenge: "",
    timeline: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company,
        service: "Diagnostic Call",
        message: form.message || form.challenge,
        role: form.role,
        company_size: form.company_size,
        segment: form.segment,
        challenge: form.challenge,
        timeline: form.timeline,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong — please try again.");
    }
  };

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Seo
        title="Book a Free Diagnostic Call — Orphion Global HR"
        description="A free 30-minute diagnostic call for GCC leaders and AI-native IT founders. Tell us your context; leave with a written read of your biggest people-risk. Proposal within one business day if there's a fit."
        path="/book-diagnostic"
      />

      <PageHero
        overline="Diagnostic call · 30 minutes · Free"
        titleA="Thirty minutes."
        titleAccent="One honest read"
        titleB="of your people function."
        intro="For GCC leaders and AI-native founders. No deck, no pitch — we ask about your context, you leave with a written view of your biggest people-risk."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline mb-6">What happens next</p>
            <ul className="space-y-5">
              {[
                "We reply within one business day to fix the slot",
                "The call runs 30 minutes — your context, our read",
                "If there's a fit, a scoped proposal (scope, timeline, price) lands within one business day of the call",
              ].map((s, i) => (
                <li key={i} className="flex gap-4 items-start">
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg leading-none mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-[var(--ink-80)] leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="md:col-span-8 space-y-8"
            data-testid="diagnostic-form"
          >
            {status === "success" ? (
              <div
                className="border border-[var(--accent)]/50 bg-[var(--bg-2)] px-6 py-8 flex items-start gap-4"
                data-testid="diagnostic-success"
              >
                <Check className="w-5 h-5 mt-0.5 text-[var(--accent)] shrink-0" strokeWidth={2} />
                <div>
                  <p className="font-display text-2xl text-[var(--ink)] mb-2">Booked in principle.</p>
                  <p className="text-sm text-[var(--ink-70)] leading-relaxed">
                    Thank you — we've received your context and will reply within one business day
                    to fix the slot, along with a short agenda and what to prepare.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="overline block mb-2">Name *</label>
                    <input required className="editorial-input" value={form.name} onChange={set("name")} placeholder="Your full name" autoComplete="name" data-testid="diag-name" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Work email *</label>
                    <input required type="email" className="editorial-input" value={form.email} onChange={set("email")} placeholder="you@company.com" autoComplete="email" data-testid="diag-email" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Company *</label>
                    <input required className="editorial-input" value={form.company} onChange={set("company")} placeholder="Company name" autoComplete="organization" data-testid="diag-company" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Your role *</label>
                    <input required className="editorial-input" value={form.role} onChange={set("role")} placeholder="e.g. Head of India Operations" data-testid="diag-role" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Company size *</label>
                    <select required className={selectCls} value={form.company_size} onChange={set("company_size")} data-testid="diag-size">
                      <option value="" disabled>Choose one…</option>
                      {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Segment *</label>
                    <select required className={selectCls} value={form.segment} onChange={set("segment")} data-testid="diag-segment">
                      <option value="" disabled>Choose one…</option>
                      {SEGMENT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Main challenge *</label>
                    <select required className={selectCls} value={form.challenge} onChange={set("challenge")} data-testid="diag-challenge">
                      <option value="" disabled>Choose one…</option>
                      {CHALLENGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-2">Timeline *</label>
                    <select required className={selectCls} value={form.timeline} onChange={set("timeline")} data-testid="diag-timeline">
                      <option value="" disabled>Choose one…</option>
                      {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">Anything else we should know?</label>
                  <textarea rows={4} className="editorial-input resize-none" value={form.message} onChange={set("message")} placeholder="Context, constraints, the thing keeping you up at night…" data-testid="diag-message" />
                </div>

                {status === "error" && (
                  <p className="text-red-600 text-sm" data-testid="diag-error">{error}</p>
                )}

                <button type="submit" disabled={status === "loading"} className="btn-pill disabled:opacity-60" data-testid="diag-submit">
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending…</span>
                    </>
                  ) : (
                    <>
                      <span>Request the call</span>
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
                    </>
                  )}
                </button>
              </>
            )}
          </motion.form>
        </div>
      </section>
    </div>
  );
}
